import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  AUTH_TOKEN_CHANGED_EVENT,
  CREDIT_REFRESH_EVENT,
  getCreditBalance
} from '../services/creditsApi';

const CreditsContext = createContext(null);

export const CreditsProvider = ({ children }) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const inFlightRef = useRef(null);
  const requestVersionRef = useRef(0);
  const balanceRef = useRef(null);

  const resetForTokenChange = useCallback(() => {
    requestVersionRef.current += 1;
    inFlightRef.current = null;
    balanceRef.current = null;
    setBalance(null);
    setError('');
    setLastUpdated(null);
  }, []);

  const refreshBalance = useCallback(async ({ force = false, silent = false } = {}) => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      resetForTokenChange();
      setLoading(false);
      return null;
    }

    if (inFlightRef.current?.token === token) {
      return inFlightRef.current.promise;
    }

    const requestVersion = requestVersionRef.current;
    if (!silent || !balanceRef.current) setLoading(true);

    const promise = getCreditBalance()
      .then((nextBalance) => {
        if (
          requestVersion === requestVersionRef.current &&
          token === sessionStorage.getItem('token')
        ) {
          balanceRef.current = nextBalance;
          setBalance(nextBalance);
          setError('');
          setLastUpdated(new Date());
        }
        return nextBalance;
      })
      .catch((requestError) => {
        if (requestError?.name === 'AbortError') return null;
        if (requestVersion === requestVersionRef.current && requestError?.code !== 'AUTH_REQUIRED') {
          setError(requestError?.message || 'Unable to load credits.');
        }
        return null;
      })
      .finally(() => {
        if (inFlightRef.current?.promise === promise) inFlightRef.current = null;
        if (requestVersion === requestVersionRef.current) setLoading(false);
      });

    inFlightRef.current = { token, promise };
    return promise;
  }, [resetForTokenChange]);

  useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  useEffect(() => {
    const handleTokenChange = () => {
      resetForTokenChange();
      refreshBalance({ force: true });
    };
    const handleStorage = (event) => {
      if (event.key === 'token') handleTokenChange();
    };
    const handleCreditRefresh = () => refreshBalance({ force: true, silent: true });
    const handleFocus = () => refreshBalance({ force: true, silent: true });

    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, handleTokenChange);
    window.addEventListener(CREDIT_REFRESH_EVENT, handleCreditRefresh);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        refreshBalance({ force: true, silent: true });
      }
    }, 60000);

    return () => {
      window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, handleTokenChange);
      window.removeEventListener(CREDIT_REFRESH_EVENT, handleCreditRefresh);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
      window.clearInterval(intervalId);
    };
  }, [refreshBalance, resetForTokenChange]);

  return (
    <CreditsContext.Provider value={{ balance, loading, error, lastUpdated, refreshBalance }}>
      {children}
    </CreditsContext.Provider>
  );
};

export const useCredits = () => {
  const value = useContext(CreditsContext);
  if (!value) throw new Error('useCredits must be used inside CreditsProvider.');
  return value;
};
